import type { ChessConstructor } from "../chess/chessTypes.ts";
import type { LessonCourse } from "../lessons/schema.ts";
import { registerWhiteCoaching } from "./whiteCoachingPacks.ts";

export const WHITE_OPENING_IDS = [
  "italian",
  "quietTwoKnights",
  "queensGambit",
  "ruyLopez",
  "english",
  "london",
  "alapin",
  "vienna",
  "scotch",
  "fourKnights",
  "reti",
  "openSicilian",
  "kingsGambit",
  "pircModern",
  "frenchAdvance",
  "scandinavian",
  "caroAdvance",
] as const;
export type WhiteOpeningId = (typeof WHITE_OPENING_IDS)[number];

type Builder = (Chess: ChessConstructor) => LessonCourse;

// Each White course body (its PGN graph and notes on side lines) is its own
// chunk, downloaded the first time the course opens, so a first visit does
// not fetch the prose of every course. Main lines live in mainlines.ts.
// Each course brings its coaching in the same chunk and registers it on arrival.
const LOADERS: Record<WhiteOpeningId, () => Promise<Builder>> = {
  italian: () =>
    import("./italian.ts").then((m) => {
      registerWhiteCoaching("italian", m.ITALIAN_COACHING);
      return m.buildItalianCourse;
    }),
  quietTwoKnights: () =>
    import("./quietTwoKnights.ts").then((m) => {
      registerWhiteCoaching("quietTwoKnights", m.QUIET_TWO_KNIGHTS_COACHING);
      return m.buildQuietTwoKnightsCourse;
    }),
  queensGambit: () =>
    import("./queensGambit.ts").then((m) => {
      registerWhiteCoaching("queensGambit", m.QUEENS_GAMBIT_COACHING);
      return m.buildQueensGambitCourse;
    }),
  ruyLopez: () =>
    import("./ruyLopez.ts").then((m) => {
      registerWhiteCoaching("ruyLopez", m.RUY_LOPEZ_COACHING);
      return m.buildRuyLopezCourse;
    }),
  english: () =>
    import("./english.ts").then((m) => {
      registerWhiteCoaching("english", m.ENGLISH_COACHING);
      return m.buildEnglishCourse;
    }),
  london: () =>
    import("./london.ts").then((m) => {
      registerWhiteCoaching("london", m.LONDON_COACHING);
      return m.buildLondonCourse;
    }),
  alapin: () =>
    import("./alapin.ts").then((m) => {
      registerWhiteCoaching("alapin", m.ALAPIN_COACHING);
      return m.buildAlapinCourse;
    }),
  vienna: () =>
    import("./vienna.ts").then((m) => {
      registerWhiteCoaching("vienna", m.VIENNA_COACHING);
      return m.buildViennaCourse;
    }),
  scotch: () =>
    import("./scotch.ts").then((m) => {
      registerWhiteCoaching("scotch", m.SCOTCH_COACHING);
      return m.buildScotchCourse;
    }),
  fourKnights: () =>
    import("./fourKnights.ts").then((m) => {
      registerWhiteCoaching("fourKnights", m.FOUR_KNIGHTS_COACHING);
      return m.buildFourKnightsCourse;
    }),
  reti: () =>
    import("./reti.ts").then((m) => {
      registerWhiteCoaching("reti", m.RETI_COACHING);
      return m.buildRetiCourse;
    }),
  openSicilian: () =>
    import("./openSicilian.ts").then((m) => {
      registerWhiteCoaching("openSicilian", m.OPEN_SICILIAN_COACHING);
      return m.buildOpenSicilianCourse;
    }),
  kingsGambit: () =>
    import("./kingsGambit.ts").then((m) => {
      registerWhiteCoaching("kingsGambit", m.KINGS_GAMBIT_COACHING);
      return m.buildKingsGambitCourse;
    }),
  pircModern: () =>
    import("./pirc.ts").then((m) => {
      registerWhiteCoaching("pircModern", m.PIRC_COACHING);
      return m.buildPircCourse;
    }),
  frenchAdvance: () =>
    import("./frenchAdvance.ts").then((m) => {
      registerWhiteCoaching("frenchAdvance", m.FRENCH_ADVANCE_COACHING);
      return m.buildFrenchAdvanceCourse;
    }),
  scandinavian: () =>
    import("./scandinavian.ts").then((m) => {
      registerWhiteCoaching("scandinavian", m.SCANDINAVIAN_COACHING);
      return m.buildScandinavianCourse;
    }),
  caroAdvance: () =>
    import("./caroAdvance.ts").then((m) => {
      registerWhiteCoaching("caroAdvance", m.CARO_ADVANCE_COACHING);
      return m.buildCaroAdvanceCourse;
    }),
};

const loaded = new Map<WhiteOpeningId, Builder>();
const pending = new Map<WhiteOpeningId, Promise<Builder>>();

export function isWhiteOpening(id: string | null | undefined): id is WhiteOpeningId {
  return Boolean(id && (WHITE_OPENING_IDS as readonly string[]).includes(id));
}

/** Download a White course's body (once). */
export function loadWhiteCourse(id: WhiteOpeningId): Promise<Builder> {
  const done = loaded.get(id);
  if (done) return Promise.resolve(done);
  let request = pending.get(id);
  if (!request) {
    request = LOADERS[id]().then((build) => {
      loaded.set(id, build);
      return build;
    });
    // A failed download (say, offline) may be retried later.
    request.catch(() => pending.delete(id));
    pending.set(id, request);
  }
  return request;
}

export function loadAllWhiteCourses(): Promise<Builder[]> {
  return Promise.all(WHITE_OPENING_IDS.map(loadWhiteCourse));
}

export function whiteCourseLoaded(id: WhiteOpeningId): boolean {
  return loaded.has(id);
}

/** The course's builder, which must already be downloaded with loadWhiteCourse. */
export function whiteCourseBuilder(id: WhiteOpeningId): Builder {
  const build = loaded.get(id);
  if (!build) throw new Error(`White course ${id} is not loaded; await loadWhiteCourse first`);
  return build;
}
